/**
 * 安全监控工具
 *
 * 目的：监控和记录认证相关的安全事件，帮助识别可疑行为
 *
 * 功能：
 * 1. 记录认证事件（登录、登出、Token刷新）
 * 2. 检测可疑模式（频繁刷新、多次失败）
 * 3. 条件化日志（开发环境详细，生产环境精简）
 *
 * 注意：这是观察性工具，不改变认证逻辑
 */

import { devError, devInfo, devWarn } from './devLog';

// 环境检测
const isDevelopment = import.meta.env.MODE === 'development';
const isSIT = import.meta.env.MODE === 'sit';

// 日志级别
type LogLevel = 'info' | 'warn' | 'error';

// 安全事件类型
type SecurityEvent =
  | 'auth_login_success'
  | 'auth_login_failed'
  | 'auth_logout'
  | 'token_refresh_success'
  | 'token_refresh_failed'
  | 'token_expired'
  | 'token_access_attempt'
  | 'suspicious_pattern';

// 事件记录结构
interface SecurityEventRecord {
  timestamp: string;
  event: SecurityEvent;
  level: LogLevel;
  details?: any;
  userAgent?: string;
  path?: string;
}

// 内存缓存（用于检测可疑模式）
const eventCache = new Map<string, number>();
const SUSPICIOUS_THRESHOLD = 5; // 5次内的相同事件视为可疑
const CACHE_WINDOW = 60000; // 60秒窗口

/**
 * 清理过期的缓存记录
 */
function cleanupCache(): void {
  const now = Date.now();
  for (const [key, timestamp] of eventCache.entries()) {
    if (now - timestamp > CACHE_WINDOW) {
      eventCache.delete(key);
    }
  }
}

/**
 * 检测是否为可疑行为
 */
function isSuspiciousPattern(event: SecurityEvent): boolean {
  cleanupCache();

  const key = `${event}_${Date.now() - (Date.now() % CACHE_WINDOW)}`;
  const count = (eventCache.get(key) || 0) + 1;
  eventCache.set(key, count);

  return count >= SUSPICIOUS_THRESHOLD;
}

/**
 * 格式化日志消息
 */
function formatLogMessage(record: SecurityEventRecord): string {
  const { timestamp, event, level, details } = record;
  const emoji = {
    info: '🔵',
    warn: '🟡',
    error: '🔴'
  }[level];

  return `${emoji} [SecurityMonitor] ${timestamp} | ${event} | ${JSON.stringify(details || {})}`;
}

/**
 * 核心日志记录函数
 */
function log(
  event: SecurityEvent,
  level: LogLevel = 'info',
  details?: any
): void {
  // 生产环境仅记录警告和错误
  if (!isDevelopment && !isSIT && level === 'info') {
    return;
  }

  const record: SecurityEventRecord = {
    timestamp: new Date().toISOString(),
    event,
    level,
    details,
  userAgent: navigator.userAgent,
  path: window.location.pathname
  };

  const messageText = formatLogMessage(record);

  // 根据级别选择console方法
  switch (level) {
    case 'error':
      devError(messageText);
      break;
    case 'warn':
      devWarn(messageText);
      break;
    default:
      devInfo(messageText);
  }

  // 生产环境可以发送到后端日志系统
  // if (import.meta.env.MODE === 'master' && level !== 'info') {
  //   sendToLogServer(record);
  // }
}

/**
 * 公共API：记录登录成功
 */
export function logLoginSuccess(userId?: string): void {
  log('auth_login_success', 'info', { userId });
}

/**
 * 公共API：记录登录失败
 */
export function logLoginFailed(reason?: string): void {
  log('auth_login_failed', 'warn', { reason });
}

/**
 * 公共API：记录登出
 */
export function logLogout(userId?: string): void {
  log('auth_logout', 'info', { userId });
}

/**
 * 公共API：记录Token刷新成功
 */
export function logTokenRefreshSuccess(): void {
  if (isSuspiciousPattern('token_refresh_success')) {
    log('suspicious_pattern', 'warn', {
      pattern: 'Frequent token refresh',
      threshold: SUSPICIOUS_THRESHOLD,
      window: `${CACHE_WINDOW / 1000}s`
    });
  }

  log('token_refresh_success', 'info');
}

/**
 * 公共API：记录Token刷新失败
 */
export function logTokenRefreshFailed(error?: any): void {
  if (isSuspiciousPattern('token_refresh_failed')) {
    log('suspicious_pattern', 'error', {
      pattern: 'Multiple token refresh failures',
      threshold: SUSPICIOUS_THRESHOLD,
      window: `${CACHE_WINDOW / 1000}s`,
      error
    });
  }

  log('token_refresh_failed', 'error', { error });
}

/**
 * 公共API：记录Token过期
 */
export function logTokenExpired(): void {
  log('token_expired', 'warn');
}

/**
 * 公共API：记录Token访问尝试
 */
export function logTokenAccessAttempt(source?: string): void {
  // 仅在开发环境记录
  if (isDevelopment) {
    log('token_access_attempt', 'info', { source });
  }
}

/**
 * 公共API：记录通用安全事件
 */
export function logSecurityEvent(
  event: SecurityEvent,
  level: LogLevel = 'info',
  details?: any
): void {
  log(event, level, details);
}

/**
 * 开发工具：打印当前缓存状态
 */
export function debugCacheState(): void {
  if (isDevelopment) {
    
  }
}
